import React from 'react';

import { getApi } from '../../../api';
import { useAuth } from '../../../context/AuthContext';
import { Fetch } from '../../../Fetch';
import { widgetStyle } from '../../../styles/constants';
import type { AuthLoginData, InitProps } from '../../../types';
import logger from '../../../utils/logger';
import { Logotype } from '../Logotype';

const encodeComponent = (s: string): string => encodeURIComponent(s);

/**
 * Init Component - Initial authentication check
 *
 * This component is shown when the app starts up. It attempts to authenticate
 * the user using a stored token from localStorage. If successful, it logs the
 * user in and navigates to the map. If unsuccessful, it navigates to login.
 */
export const Init: React.FC<InitProps> = ({ setView }) => {
  const { login, token } = useAuth();

  // If no token exists, go directly to login screen
  React.useEffect(() => {
    if (!token || token === 'null') {
      setView('login');
    }
  }, [token, setView]);

  /**
   * Handle authentication error - navigate to offline view
   */
  const handleError = (): void => {
    setView('offline');
  };

  /**
   * Handle authentication result from server
   */
  const handleResult = (data: unknown): void => {
    const result = data as AuthLoginData;
    logger.debug(result);
    if (result.token && result.account) {
      // Authentication successful - log in user and go to map
      login(result);
      setView('map');
    } else {
      // Authentication failed - go to login screen
      setView('login');
    }
  };

  // Don't render Fetch component if no valid token
  if (!token || token === 'null') {
    return null;
  }

  return (
    <div style={{ minWidth: '50vmin' }}>
      <Logotype />
      <hr />
      <div style={widgetStyle}>Connecting...</div>
      <Fetch
        submit={true}
        onError={handleError}
        onResult={handleResult}
        api={`${getApi()}/ping?token=${encodeComponent(token)}`}
      />
    </div>
  );
};

export default Init;
